import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Spinner, Button, Card, Badge, Row, Col, ProgressBar, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import Evaluacion from './Evaluacion';
import CertificadoButton from './CertificadoButton';
import ValoracionCapacitacion from './ValoracionCapacitacion';
import { FaFilePdf, FaDownload, FaArrowLeft, FaCheckCircle, FaPlayCircle, FaClipboardCheck, FaClock, FaTrophy, FaCertificate, FaStar } from 'react-icons/fa';
import '../styles/VerModulo.css';

const API_URL = (process.env.REACT_APP_API_URL || "/").replace(/\/$/, '');

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

// Componente para renderizar el contenido según el tipo
const ContenidoModulo = ({ modulo }) => {
  const tipoContenido = modulo.tipo_contenido || 'youtube';
  const url = modulo.video_url;

  // YouTube
  if (tipoContenido === 'youtube' || (!tipoContenido && url?.includes('youtube'))) {
    return (
      <div className="video-wrapper">
        <div className="ratio ratio-16x9">
          <iframe
            src={url.replace('watch?v=', 'embed/')}
            title="Video de capacitación"
            allowFullScreen
          />
        </div>
      </div>
    );
  }

  // Video local
  if (tipoContenido === 'video') {
    const videoUrl = url.startsWith('http') ? url : `${API_URL}${url.startsWith('/') ? url.slice(1) : url}`;
    return (
      <div className="video-wrapper">
        <div className="ratio ratio-16x9">
          <video 
            controls 
            controlsList="nodownload"
          >
            <source src={videoUrl} type="video/mp4" />
            Tu navegador no soporta la reproducción de video.
          </video>
        </div>
      </div>
    );
  }

  // PDF
  if (tipoContenido === 'pdf') {
    const pdfUrl = url.startsWith('http') ? url : `${API_URL}${url.startsWith('/') ? url.slice(1) : url}`;
    return (
      <div className="pdf-wrapper">
        <div className="pdf-header">
          <span className="pdf-label">
            <FaFilePdf className="text-danger me-2" size={20} />
            Documento de estudio
          </span>
          <Button 
            variant="outline-light" 
            size="sm"
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaDownload className="me-1" /> Abrir en nueva pestaña
          </Button>
        </div>
        <iframe
          src={pdfUrl}
          title="PDF de capacitación"
          className="pdf-viewer"
        />
      </div>
    );
  }

  // Fallback
  return (
    <div className="video-wrapper">
      <div className="ratio ratio-16x9">
        <iframe
          src={url?.replace('watch?v=', 'embed/') || ''}
          title="Video de capacitación"
          allowFullScreen
        />
      </div>
    </div>
  );
};

// Componente de estado de evaluación
const EstadoEvaluacion = ({ 
  tieneEvaluacion, 
  evaluacionRespondida, 
  porcentajeObtenido, 
  requiereAprobacion,
  onIniciarEvaluacion 
}) => {
  if (!requiereAprobacion) {
    return (
      <Card className="evaluacion-card evaluacion-info">
        <Card.Body className="text-center py-4">
          <FaPlayCircle size={40} className="mb-3 text-info" />
          <h5>Módulo informativo</h5>
          <p className="text-muted mb-0">Este módulo no requiere evaluación</p>
        </Card.Body>
      </Card>
    );
  }

  if (!tieneEvaluacion) {
    return (
      <Card className="evaluacion-card evaluacion-pending">
        <Card.Body className="text-center py-4">
          <FaClock size={40} className="mb-3 text-warning" />
          <h5>Evaluación pendiente</h5>
          <p className="text-muted mb-0">Este módulo aún no tiene evaluación cargada</p>
        </Card.Body>
      </Card>
    );
  }

  // Aprobó: mostrar resultado y NO permitir reintentos
  if (evaluacionRespondida && porcentajeObtenido >= 60) {
    return (
      <Card className="evaluacion-card evaluacion-success">
        <Card.Body className="text-center py-4">
          <div className="success-icon-wrapper mb-3">
            <FaTrophy size={40} className="text-success" />
          </div>
          <h5 className="text-success">¡Evaluación aprobada!</h5>
          <div className="score-display">
            <span className="score-number">{porcentajeObtenido}%</span>
            <ProgressBar 
              now={porcentajeObtenido} 
              variant="success" 
              className="mt-2"
              style={{ height: '8px' }}
            />
          </div>
          <p className="text-muted mt-3 mb-0">
            <FaCheckCircle className="me-1" /> Resultado registrado correctamente
          </p>
        </Card.Body>
      </Card>
    );
  }

  if (porcentajeObtenido !== null) {
    return (
      <Card className="evaluacion-card evaluacion-retry">
        <Card.Body className="text-center py-4">
          <h5 className="text-warning">Intento anterior</h5>
          <div className="score-display">
            <span className="score-number text-warning">{porcentajeObtenido}%</span>
            <ProgressBar 
              now={porcentajeObtenido} 
              variant="warning" 
              className="mt-2"
              style={{ height: '8px' }}
            />
          </div>
          <p className="text-muted mt-3">Necesitás al menos 60% para aprobar</p>
          <Button 
            variant="warning" 
            size="lg"
            className="btn-evaluacion"
            onClick={onIniciarEvaluacion}
          >
            <FaClipboardCheck className="me-2" />
            Reintentar evaluación
          </Button>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="evaluacion-card evaluacion-ready">
      <Card.Body className="text-center py-4">
        <FaClipboardCheck size={40} className="mb-3 text-primary" />
        <h5>Evaluación disponible</h5>
        <p className="text-muted">Completá la evaluación para aprobar este módulo</p>
        <Button 
          variant="primary" 
          size="lg"
          className="btn-evaluacion"
          onClick={onIniciarEvaluacion}
        >
          <FaClipboardCheck className="me-2" />
          Comenzar evaluación
        </Button>
      </Card.Body>
    </Card>
  );
};


const VerModulo = ({ usuario: usuarioProp }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  
  // Leer usuario de localStorage (más confiable que el prop)
  const usuario = JSON.parse(localStorage.getItem('user'));
  
  const [modulo, setModulo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mostrarEvaluacion, setMostrarEvaluacion] = useState(false);
  const [tieneEvaluacion, setTieneEvaluacion] = useState(false);
  const [evaluacionRespondida, setEvaluacionRespondida] = useState(false);
  const [porcentajeObtenido, setPorcentajeObtenido] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/capacitaciones/modulo/${id}`, {
      headers: getAuthHeaders()
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) setModulo(data.data);
        setLoading(false);
      });
  }, [id]);

  useEffect(() => {
    fetch(`${API_URL}/api/capacitaciones/evaluacion/existe/${id}`, {
      headers: getAuthHeaders()
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) setTieneEvaluacion(data.tieneEvaluacion);
      });
  }, [id]);

  useEffect(() => {
    if (!usuario?.Legajo) return;

    fetch(`${API_URL}/api/capacitaciones/evaluacion/estado/${id}/${usuario.Legajo}`, {
      headers: getAuthHeaders()
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setEvaluacionRespondida(data.aprobado);
          setPorcentajeObtenido(data.porcentaje);
          console.log('📊 Estado de evaluación:', {
            aprobado: data.aprobado,
            porcentaje: data.porcentaje,
            mostrarCertificado: data.aprobado && data.porcentaje >= 60
          });
        }
      });
  }, [id, usuario]);

  if (loading || !modulo) return (
    <div className="ver-modulo-page">
      <Navbar />
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">Cargando módulo...</p>
      </Container>
    </div>
  );

  return (
    <div className="ver-modulo-page">
      <Navbar />
      
      <Container className="py-4">
        {/* Header con navegación */}
        <div className="modulo-header mb-4">
          <Button 
            variant="link" 
            className="btn-volver p-0"
            onClick={() => navigate('/home/ver-capacitaciones')}
          >
            <FaArrowLeft className="me-2" />
            Volver a capacitaciones
          </Button>
        </div>

        <Row className="g-4">
          {/* Columna principal - Contenido */}
          <Col lg={8}>
            <Card className="modulo-card">
              {/* Título del módulo */}
              <Card.Header className="modulo-card-header">
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                  <div>
                    <Badge bg="secondary" className="mb-2">Módulo {modulo.orden}</Badge>
                    <h4 className="modulo-titulo mb-0">{modulo.titulo}</h4>
                  </div>
                  {evaluacionRespondida && (
                    <Badge bg="success" className="badge-estado">
                      <FaCheckCircle className="me-1" /> Completado
                    </Badge>
                  )}
                </div>
              </Card.Header>

              {/* Contenido del módulo */}
              <Card.Body className="p-0">
                <ContenidoModulo modulo={modulo} />
              </Card.Body>
            </Card>

            {/* Archivo de refuerzo (PDF adicional) */}
            {modulo.archivo_refuerzo_url && (
              <Card className="mt-3 border-danger">
                <Card.Body className="py-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      <FaFilePdf className="text-danger me-3" size={32} />
                      <div>
                        <h6 className="mb-0">Material de Refuerzo</h6>
                        <small className="text-muted">{modulo.archivo_refuerzo_nombre || 'Documento complementario'}</small>
                      </div>
                    </div>
                    <Button 
                      variant="danger"
                      size="sm"
                      href={`${API_URL}${modulo.archivo_refuerzo_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FaDownload className="me-2" />
                      Descargar PDF
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* Valoración del módulo */}
            {modulo.requiere_valoracion === 1 && (
              <div className="mt-4">
                <ValoracionCapacitacion 
                  moduloId={id}
                  legajo={usuario?.Legajo}
                  onValoracionGuardada={(valoracion) => {
                    console.log('✅ Valoración guardada:', valoracion);
                  }}
                />
              </div>
            )}

            {/* SECCIÓN DE CERTIFICADO - Visible solo si está aprobado */}
            {/* {evaluacionRespondida && porcentajeObtenido >= 60 && (
              <Card className="mt-4 border-success" style={{ borderWidth: '2px' }}>
                {console.log('🎓 Mostrando sección de certificado:', {
                  evaluacionRespondida,
                  porcentajeObtenido,
                  usuario: usuario?.Nombre,
                  legajo: usuario?.Legajo,
                  modulo: modulo?.titulo
                })}
                <Card.Body className="text-center py-4">
                  <div className="mb-3">
                    <FaCertificate size={50} style={{ color: '#28a745' }} />
                  </div>
                  <h5 className="text-success mb-2">¡Capacitación Aprobada!</h5>
                  <p className="text-muted mb-3">
                    Obtuviste <strong>{porcentajeObtenido}%</strong> en la evaluación
                  </p>
                  <CertificadoButton
                    moduloId={id}
                    nombreCompleto={usuario?.Nombre || ''}
                    legajo={usuario?.Legajo || ''}
                    nombreCapacitacion={modulo?.titulo || 'Capacitación'}
                    categoriaCapacitacion={modulo?.categoria_nombre || 'General'}
                    notaObtenida={porcentajeObtenido}
                    fechaAprobacion={new Date().toLocaleDateString('es-AR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                    duracionHoras={modulo?.duracion_horas || '2'}
                  />
                </Card.Body>
              </Card>
            )} */}
            
            {/* Debug: Mostrar por qué no aparece el certificado */}
            {console.log('🔍 Estado actual certificado:', {
              evaluacionRespondida,
              porcentajeObtenido,
              cumpleCondicion: evaluacionRespondida && porcentajeObtenido >= 60
            })}
          </Col>

          {/* Columna lateral - Evaluación */}
          <Col lg={4}>
            {!mostrarEvaluacion ? (
              <EstadoEvaluacion
                tieneEvaluacion={tieneEvaluacion}
                evaluacionRespondida={evaluacionRespondida}
                porcentajeObtenido={porcentajeObtenido}
                requiereAprobacion={modulo.requiere_aprobacion}
                onIniciarEvaluacion={() => setMostrarEvaluacion(true)}
              />
            ) : (
              <Card className="evaluacion-card evaluacion-active">
                <Card.Header className="evaluacion-header">
                  <h5 className="mb-0">
                    <FaClipboardCheck className="me-2" />
                    Evaluación
                  </h5>
                  <Button 
                    variant="link" 
                    size="sm" 
                    className="text-white p-0"
                    onClick={() => setMostrarEvaluacion(false)}
                  >
                    Cancelar
                  </Button>
                </Card.Header>
                <Card.Body>
                  <Evaluacion 
                    moduloId={id} 
                    usuario={usuario}
                    onComplete={(aprobado, porcentaje) => {
                      setEvaluacionRespondida(aprobado);
                      setPorcentajeObtenido(porcentaje);
                      // Cerrar el modal después de aprobar para mostrar el certificado en la sección principal
                      if (aprobado) {
                        setTimeout(() => setMostrarEvaluacion(false), 2000);
                      }
                    }}
                  />
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default VerModulo;